import { useFlags } from "launchdarkly-react-client-sdk";
import { gt, lt } from "lodash";
import { useContext, useMemo } from "react";

import {
  Integrations,
  IntegrationsContext,
} from "../contexts/IntegrationsContext";
import { FilterConfig, TimeRange } from "../types.ts";
import { useRequestFilters } from "./useRequestFilters";

const createFilterFunction = <T>(config: FilterConfig<T>) => {
  return (
    items: T[],
    filters: {
      searchQuery: string | null;
      requestorFilter: string | null;
      approverFilter: string | null;
      resourceFilter: string | null;
      parentFilter: string | null;
      requestedFilter: TimeRange;
      approvedFilter: TimeRange;
    },
    context: { integrations: Integrations; flags: Record<string, boolean> }
  ) => {
    return items.filter((item) => {
      // Search query
      if (filters.searchQuery !== null) {
        const normalizedQuery = filters.searchQuery.toLowerCase();
        const searchFields = [
          config.getRequestor(item),
          ...config.getApproverSearchFields(item),
          config.getResourceDescription(item),
          config.getLocation(item, context),
        ];
        if (
          !searchFields.some(
            (field) =>
              typeof field === "string" &&
              field?.toLowerCase().includes(normalizedQuery)
          )
        ) {
          return false;
        }
      }

      // Requestor filter
      if (
        filters.requestorFilter &&
        config.getRequestor(item) !== filters.requestorFilter
      ) {
        return false;
      }

      // Approver filter
      if (
        filters.approverFilter &&
        config.getApprover(item) !== filters.approverFilter
      ) {
        return false;
      }

      // Resource filter
      if (
        filters.resourceFilter &&
        config.getResourceDescription(item) !== filters.resourceFilter
      ) {
        return false;
      }

      // Parent filter
      if (filters.parentFilter) {
        const location = config.getLocation(item, context);
        if (location !== filters.parentFilter) {
          return false;
        }
      }

      // Requested date filter
      const [requestedAfter, requestedBefore] = filters.requestedFilter;
      const requestedTimestamp = config.getRequestedTimestamp(item);
      if (requestedAfter && !gt(requestedTimestamp, requestedAfter.getTime())) {
        return false;
      }
      if (
        requestedBefore &&
        !lt(requestedTimestamp, requestedBefore.getTime())
      ) {
        return false;
      }

      // Approved date filter (if applicable)
      if (config.getApprovedTimestamp) {
        const [approvedAfter, approvedBefore] = filters.approvedFilter;
        const approvedTimestamp = config.getApprovedTimestamp(item);
        if (
          approvedAfter &&
          approvedTimestamp &&
          !gt(approvedTimestamp, approvedAfter.getTime())
        ) {
          return false;
        }
        if (
          approvedBefore &&
          approvedTimestamp &&
          !lt(approvedTimestamp, approvedBefore.getTime())
        ) {
          return false;
        }
      }

      return true;
    });
  };
};

/**
 * Hook to memoize the filters specified by the given filter config.
 */
export const useFilteredData = <T>(filterConfig: FilterConfig<T>) => {
  const integrations = useContext(IntegrationsContext);
  const flags = useFlags();

  const filters = useRequestFilters();

  const filterFunction = useMemo(
    () => createFilterFunction(filterConfig),
    [filterConfig]
  );

  // Normalize undefined to null to ensure type safety
  // across the filter code
  const filterParams = useMemo(
    () => ({
      searchQuery: filters.searchQuery,
      requestorFilter: filters.requestorFilter ?? null,
      approverFilter: filters.approverFilter ?? null,
      resourceFilter: filters.resourceFilter ?? null,
      parentFilter: filters.parentFilter ?? null,
      requestedFilter: filters.requestedFilter,
      approvedFilter: filters.approvedFilter,
    }),
    [
      filters.searchQuery,
      filters.requestorFilter,
      filters.approverFilter,
      filters.resourceFilter,
      filters.parentFilter,
      filters.requestedFilter,
      filters.approvedFilter,
    ]
  );

  const context = useMemo(
    () => ({ integrations, flags }),
    [integrations, flags]
  );

  const applyFilters = useMemo(
    () => (items: T[]) => filterFunction(items, filterParams, context),
    [filterFunction, filterParams, context]
  );

  return {
    filters: {
      searchQuery: filters.searchQuery,
      setSearchQuery: filters.setSearchQuery,
      requestedFilter: filters.requestedFilter,
      setRequestedFilter: filters.setRequestedFilter,
      approvedFilter: filters.approvedFilter,
      setApprovedFilter: filters.setApprovedFilter,
      requestorFilter: filters.requestorFilter,
      setRequestorFilter: filters.setRequestorFilter,
      approverFilter: filters.approverFilter,
      setApproverFilter: filters.setApproverFilter,
      resourceFilter: filters.resourceFilter,
      setResourceFilter: filters.setResourceFilter,
      parentFilter: filters.parentFilter,
      setParentFilter: filters.setParentFilter,
    },
    applyFilters,
    resetFilters: filters.resetFilters,
  };
};
